import React from "react";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  CircleOff,
  Megaphone,
  Search,
  Table2,
  TrendingDown,
  TrendingUp,
  Wine,
  type LucideIcon,
} from "lucide-react";
import { BarChart, type BarChartDataset } from "../components/BarChart";
import {
  HESS_ALL_SKU_CSV_PATH,
  type HessAllSkuYoYProduct,
  type HessCampaignAction,
  type HessSkuYoYDashboardData,
  type HessSkuYoYMonth,
  type HessSkuYoYTotals,
  type HessTargetSkuYoYProduct,
  parseHessSkuYoYCsv,
} from "./hessSkuYoYData";

type MetricMode = "units" | "sales";
type ProductFilter = "all" | HessCampaignAction;
type SortMode = "impact" | "pct" | "current";

const PROMOTED_COLOR = "#0F766E";
const PROMOTED_SOFT = "rgba(15, 118, 110, 0.22)";
const TURNOFF_COLOR = "#9F1239";
const TURNOFF_SOFT = "rgba(159, 18, 57, 0.22)";
const PORTFOLIO_COLOR = "#334155";
const YAGO_COLOR = "#A8A29E";

const formatMoney = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const formatNumber = (value: number) =>
  Math.round(value).toLocaleString("en-US");

const formatCompactMoney = (value: number) => {
  const absValue = Math.abs(value);
  if (absValue >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (absValue >= 1_000) return `$${(value / 1_000).toFixed(0)}k`;
  return formatMoney(value);
};

const formatSignedMoney = (value: number) =>
  `${value > 0 ? "+" : ""}${formatMoney(value)}`;

const formatSignedNumber = (value: number) =>
  `${value > 0 ? "+" : ""}${formatNumber(value)}`;

const formatPct = (value: number | null) => {
  if (value === null || !Number.isFinite(value)) return "n/a";
  return `${value > 0 ? "+" : ""}${(value * 100).toFixed(1)}%`;
};

const monthLabel = (isoDate: string) =>
  new Date(`${isoDate}T00:00:00`).toLocaleString("en-US", {
    month: "short",
    year: "2-digit",
  });

const monthRangeLabel = (start: string, end: string) =>
  `${monthLabel(start)} to ${monthLabel(end)}`;

const totalsFromProducts = (
  products: Array<
    Pick<
      HessSkuYoYTotals,
      "currentSales" | "yagoSales" | "currentUnits" | "yagoUnits"
    >
  >,
): HessSkuYoYTotals => {
  const currentSales = products.reduce((sum, product) => sum + product.currentSales, 0);
  const yagoSales = products.reduce((sum, product) => sum + product.yagoSales, 0);
  const currentUnits = products.reduce((sum, product) => sum + product.currentUnits, 0);
  const yagoUnits = products.reduce((sum, product) => sum + product.yagoUnits, 0);

  return {
    currentSales,
    yagoSales,
    salesDelta: currentSales - yagoSales,
    salesDeltaPct: yagoSales === 0 ? null : (currentSales - yagoSales) / yagoSales,
    currentUnits,
    yagoUnits,
    unitDelta: currentUnits - yagoUnits,
    unitDeltaPct: yagoUnits === 0 ? null : (currentUnits - yagoUnits) / yagoUnits,
  };
};

const cohortMonthlyTotals = (
  products: HessTargetSkuYoYProduct[],
  months: HessSkuYoYMonth[],
) =>
  months.map((month) => {
    const productMonthRows = products
      .map((product) =>
        product.months.find((item) => item.monthStart === month.monthStart),
      )
      .filter((item): item is HessSkuYoYMonth => Boolean(item));
    return {
      monthStart: month.monthStart,
      ...totalsFromProducts(productMonthRows),
    };
  });

const metricDelta = (
  item: HessSkuYoYTotals,
  metricMode: MetricMode,
) => (metricMode === "units" ? item.unitDelta : item.salesDelta);

const metricCurrent = (
  item: HessSkuYoYTotals,
  metricMode: MetricMode,
) => (metricMode === "units" ? item.currentUnits : item.currentSales);

const metricYago = (
  item: HessSkuYoYTotals,
  metricMode: MetricMode,
) => (metricMode === "units" ? item.yagoUnits : item.yagoSales);

const metricPct = (
  item: HessSkuYoYTotals,
  metricMode: MetricMode,
) => (metricMode === "units" ? item.unitDeltaPct : item.salesDeltaPct);

const formatMetricValue = (value: number, metricMode: MetricMode) =>
  metricMode === "units" ? formatNumber(value) : formatMoney(value);

const formatSignedMetricValue = (value: number, metricMode: MetricMode) =>
  metricMode === "units" ? formatSignedNumber(value) : formatSignedMoney(value);

const actionLabel = (action: HessCampaignAction) => {
  if (action === "promoted") return "Kept / added";
  if (action === "turned_off") return "Turned off";
  return "Other SKU";
};

const actionClasses = (action: HessCampaignAction) => {
  if (action === "promoted") {
    return "border-teal-700/25 bg-teal-50 text-teal-900 dark:border-teal-300/30 dark:bg-teal-950/40 dark:text-teal-100";
  }
  if (action === "turned_off") {
    return "border-rose-700/25 bg-rose-50 text-rose-900 dark:border-rose-300/30 dark:bg-rose-950/40 dark:text-rose-100";
  }
  return "border-stone-300 bg-stone-100 text-stone-700 dark:border-stone-600 dark:bg-stone-800 dark:text-stone-200";
};

const deltaColorClass = (value: number) =>
  value > 0
    ? "text-teal-700 dark:text-teal-300"
    : value < 0
      ? "text-rose-700 dark:text-rose-300"
      : "text-stone-600 dark:text-stone-300";

interface KpiTileProps {
  title: string;
  value: string;
  delta: string;
  detail: string;
  icon: LucideIcon;
  tone: "portfolio" | "good" | "risk";
}

const KpiTile: React.FC<KpiTileProps> = ({
  title,
  value,
  delta,
  detail,
  icon: Icon,
  tone,
}) => {
  const toneClasses = {
    portfolio:
      "border-slate-300 bg-white text-slate-950 dark:border-stone-700 dark:bg-[#211d1c] dark:text-stone-50",
    good: "border-teal-700/30 bg-teal-50 text-teal-950 dark:border-teal-300/30 dark:bg-teal-950/35 dark:text-teal-50",
    risk: "border-rose-700/30 bg-rose-50 text-rose-950 dark:border-rose-300/30 dark:bg-rose-950/35 dark:text-rose-50",
  };

  return (
    <div className={`rounded-lg border p-5 shadow-sm ${toneClasses[tone]}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] opacity-70">
          {title}
        </p>
        <Icon className="h-5 w-5 opacity-70" aria-hidden="true" />
      </div>
      <div className="mt-4 text-3xl font-semibold tracking-normal">{value}</div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold">{delta}</span>
        <span className="opacity-70">{detail}</span>
      </div>
    </div>
  );
};

interface CohortPanelProps {
  title: string;
  subtitle: string;
  icon: LucideIcon;
  products: HessTargetSkuYoYProduct[];
  totals: HessSkuYoYTotals;
  metricMode: MetricMode;
  color: string;
  softColor: string;
}

const CohortPanel: React.FC<CohortPanelProps> = ({
  title,
  subtitle,
  icon: Icon,
  products,
  totals,
  metricMode,
  color,
  softColor,
}) => {
  const maxAbsDelta = Math.max(
    1,
    ...products.map((product) => Math.abs(metricDelta(product, metricMode))),
  );

  return (
    <section className="rounded-lg border border-stone-300 bg-white p-5 shadow-sm dark:border-stone-700 dark:bg-[#211d1c]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5" style={{ color }} aria-hidden="true" />
            <h2 className="text-lg font-semibold text-stone-950 dark:text-stone-50">
              {title}
            </h2>
          </div>
          <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">
            {subtitle}
          </p>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-semibold ${deltaColorClass(metricDelta(totals, metricMode))}`}>
            {formatSignedMetricValue(metricDelta(totals, metricMode), metricMode)}
          </div>
          <div className="text-xs uppercase tracking-[0.16em] text-stone-500 dark:text-stone-400">
            {formatPct(metricPct(totals, metricMode))}
          </div>
        </div>
      </div>

      <div className="mt-5 divide-y divide-stone-200 dark:divide-stone-700">
        {products.map((product) => {
          const delta = metricDelta(product, metricMode);
          const width = Math.max(8, (Math.abs(delta) / maxAbsDelta) * 100);

          return (
            <div key={product.id} className="py-4 first:pt-0 last:pb-0">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="truncate font-medium text-stone-950 dark:text-stone-50">
                    {product.name}
                  </div>
                  <div className="mt-1 font-mono text-xs text-stone-500 dark:text-stone-400">
                    {product.sku}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className={`font-semibold ${deltaColorClass(delta)}`}>
                    {formatSignedMetricValue(delta, metricMode)}
                  </div>
                  <div className="text-xs text-stone-500 dark:text-stone-400">
                    {formatPct(metricPct(product, metricMode))}
                  </div>
                </div>
              </div>
              <div className="mt-3 h-2 rounded-full bg-stone-100 dark:bg-stone-800">
                <div
                  className="h-2 rounded-full"
                  style={{
                    width: `${width}%`,
                    backgroundColor: delta >= 0 ? color : softColor,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

interface TargetSkuTableProps {
  products: HessTargetSkuYoYProduct[];
  metricMode: MetricMode;
}

const TargetSkuTable: React.FC<TargetSkuTableProps> = ({
  products,
  metricMode,
}) => (
  <section className="rounded-lg border border-stone-300 bg-white p-5 shadow-sm dark:border-stone-700 dark:bg-[#211d1c]">
    <div className="flex items-center gap-2">
      <Table2 className="h-5 w-5 text-slate-700 dark:text-stone-300" aria-hidden="true" />
      <h2 className="text-lg font-semibold text-stone-950 dark:text-stone-50">
        Target SKU Matrix
      </h2>
    </div>
    <div className="mt-4 overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-stone-200 text-left text-xs uppercase tracking-[0.14em] text-stone-500 dark:border-stone-700 dark:text-stone-400">
            <th className="py-3 pr-4">SKU</th>
            <th className="py-3 pr-4">Product</th>
            <th className="py-3 pr-4">Campaign</th>
            <th className="py-3 pr-4 text-right">Current</th>
            <th className="py-3 pr-4 text-right">YAGO</th>
            <th className="py-3 pr-4 text-right">YoY Δ</th>
            <th className="py-3 pr-0 text-right">YoY %</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product) => {
            const delta = metricDelta(product, metricMode);
            return (
              <tr
                key={product.id}
                className="border-b border-stone-100 last:border-0 dark:border-stone-800"
              >
                <td className="py-3 pr-4 align-top font-mono text-xs text-stone-600 dark:text-stone-300">
                  {product.sku}
                </td>
                <td className="max-w-[18rem] py-3 pr-4 align-top">
                  <div className="font-medium text-stone-950 dark:text-stone-50">
                    {product.name}
                  </div>
                  <div className="mt-1 text-xs text-stone-500 dark:text-stone-400">
                    Source code: {product.sourceCodes.join(", ") || "not preserved"}
                  </div>
                </td>
                <td className="py-3 pr-4 align-top">
                  <span
                    className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${actionClasses(product.campaignAction)}`}
                  >
                    {actionLabel(product.campaignAction)}
                  </span>
                </td>
                <td className="py-3 pr-4 text-right align-top text-stone-800 dark:text-stone-100">
                  {formatMetricValue(metricCurrent(product, metricMode), metricMode)}
                </td>
                <td className="py-3 pr-4 text-right align-top text-stone-500 dark:text-stone-400">
                  {formatMetricValue(metricYago(product, metricMode), metricMode)}
                </td>
                <td className={`py-3 pr-4 text-right align-top font-semibold ${deltaColorClass(delta)}`}>
                  {formatSignedMetricValue(delta, metricMode)}
                </td>
                <td className={`py-3 pr-0 text-right align-top font-semibold ${deltaColorClass(delta)}`}>
                  {formatPct(metricPct(product, metricMode))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </section>
);

interface AllSkuTableProps {
  products: HessAllSkuYoYProduct[];
  metricMode: MetricMode;
}

const AllSkuTable: React.FC<AllSkuTableProps> = ({ products, metricMode }) => {
  const [filter, setFilter] = React.useState<ProductFilter>("all");
  const [sortMode, setSortMode] = React.useState<SortMode>("impact");
  const [query, setQuery] = React.useState("");

  const filteredProducts = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return products
      .filter((product) => filter === "all" || product.campaignAction === filter)
      .filter((product) => {
        if (!normalizedQuery) return true;
        return `${product.brand} ${product.name} ${product.sourceCodes.join(" ")}`
          .toLowerCase()
          .includes(normalizedQuery);
      })
      .sort((a, b) => {
        if (sortMode === "current") {
          return metricCurrent(b, metricMode) - metricCurrent(a, metricMode);
        }
        if (sortMode === "pct") {
          return (metricPct(b, metricMode) ?? -Infinity) - (metricPct(a, metricMode) ?? -Infinity);
        }
        return Math.abs(metricDelta(b, metricMode)) - Math.abs(metricDelta(a, metricMode));
      });
  }, [filter, metricMode, products, query, sortMode]);

  const filterOptions: Array<{ value: ProductFilter; label: string }> = [
    { value: "all", label: "All" },
    { value: "promoted", label: "Kept / added" },
    { value: "turned_off", label: "Turned off" },
    { value: "other", label: "Other" },
  ];

  const sortOptions: Array<{ value: SortMode; label: string }> = [
    { value: "impact", label: "Impact" },
    { value: "pct", label: "YoY %" },
    { value: "current", label: "Current" },
  ];

  return (
    <section className="rounded-lg border border-stone-300 bg-white p-5 shadow-sm dark:border-stone-700 dark:bg-[#211d1c]">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Wine className="h-5 w-5 text-slate-700 dark:text-stone-300" aria-hidden="true" />
            <h2 className="text-lg font-semibold text-stone-950 dark:text-stone-50">
              All SKU Performance
            </h2>
          </div>
          <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">
            {filteredProducts.length} product rows after current filters
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" aria-hidden="true" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search SKUs"
              className="h-10 w-full rounded-lg border border-stone-300 bg-white pl-9 pr-3 text-sm text-stone-900 outline-none ring-offset-2 transition focus:ring-2 focus:ring-teal-700 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-50 sm:w-56"
            />
          </div>
          <div className="inline-flex rounded-lg border border-stone-300 bg-stone-100 p-1 dark:border-stone-700 dark:bg-stone-900">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={sortMode === option.value}
                onClick={() => setSortMode(option.value)}
                className={`h-8 min-w-20 rounded-md px-3 text-xs font-semibold transition ${
                  sortMode === option.value
                    ? "bg-white text-stone-950 shadow-sm dark:bg-stone-700 dark:text-white"
                    : "text-stone-600 hover:text-stone-950 dark:text-stone-300 dark:hover:text-white"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {filterOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={filter === option.value}
            onClick={() => setFilter(option.value)}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
              filter === option.value
                ? "border-slate-900 bg-slate-900 text-white dark:border-stone-100 dark:bg-stone-100 dark:text-stone-950"
                : "border-stone-300 bg-white text-stone-700 hover:border-stone-500 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-200"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-stone-200 text-left text-xs uppercase tracking-[0.14em] text-stone-500 dark:border-stone-700 dark:text-stone-400">
              <th className="py-3 pr-4">Product</th>
              <th className="py-3 pr-4">Brand</th>
              <th className="py-3 pr-4">Campaign</th>
              <th className="py-3 pr-4 text-right">Current</th>
              <th className="py-3 pr-4 text-right">YAGO</th>
              <th className="py-3 pr-4 text-right">YoY Δ</th>
              <th className="py-3 pr-0 text-right">YoY %</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map((product) => {
              const delta = metricDelta(product, metricMode);
              return (
                <tr
                  key={product.id}
                  className="border-b border-stone-100 last:border-0 dark:border-stone-800"
                >
                  <td className="max-w-[22rem] py-3 pr-4 align-top">
                    <div className="font-medium text-stone-950 dark:text-stone-50">
                      {product.name}
                    </div>
                    <div className="mt-1 font-mono text-xs text-stone-500 dark:text-stone-400">
                      {product.sourceCodes.join(", ")}
                    </div>
                  </td>
                  <td className="py-3 pr-4 align-top text-stone-600 dark:text-stone-300">
                    {product.brand}
                  </td>
                  <td className="py-3 pr-4 align-top">
                    <span
                      className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${actionClasses(product.campaignAction)}`}
                    >
                      {actionLabel(product.campaignAction)}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-right align-top text-stone-800 dark:text-stone-100">
                    {formatMetricValue(metricCurrent(product, metricMode), metricMode)}
                  </td>
                  <td className="py-3 pr-4 text-right align-top text-stone-500 dark:text-stone-400">
                    {formatMetricValue(metricYago(product, metricMode), metricMode)}
                  </td>
                  <td className={`py-3 pr-4 text-right align-top font-semibold ${deltaColorClass(delta)}`}>
                    {formatSignedMetricValue(delta, metricMode)}
                  </td>
                  <td className={`py-3 pr-0 text-right align-top font-semibold ${deltaColorClass(delta)}`}>
                    {formatPct(metricPct(product, metricMode))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export const HessSkuYoYPage: React.FC = () => {
  const [data, setData] = React.useState<HessSkuYoYDashboardData | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [metricMode, setMetricMode] = React.useState<MetricMode>("units");

  React.useEffect(() => {
    let active = true;

    fetch(HESS_ALL_SKU_CSV_PATH)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Could not load ${HESS_ALL_SKU_CSV_PATH}`);
        }
        return response.text();
      })
      .then((csvText) => parseHessSkuYoYCsv(csvText, "Hess_all-sku.csv"))
      .then((parsedData) => {
        if (active) {
          setData(parsedData);
          setError(null);
        }
      })
      .catch((loadError) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : "Unknown CSV load error");
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const analysis = React.useMemo(() => {
    if (!data) return null;

    const turnedOffProducts = data.targetProducts.filter(
      (product) => product.campaignAction === "turned_off",
    );
    const promotedProducts = data.targetProducts.filter(
      (product) => product.campaignAction === "promoted",
    );
    const turnedOffTotals = totalsFromProducts(turnedOffProducts);
    const promotedTotals = totalsFromProducts(promotedProducts);
    const turnedOffMonthly = cohortMonthlyTotals(turnedOffProducts, data.monthlyTotals);
    const promotedMonthly = cohortMonthlyTotals(promotedProducts, data.monthlyTotals);
    const allTargetsTotals = totalsFromProducts(data.targetProducts);
    const strongestTarget = [...data.targetProducts].sort(
      (a, b) => metricDelta(b, "units") - metricDelta(a, "units"),
    )[0];
    const weakestTarget = [...data.targetProducts].sort(
      (a, b) => metricDelta(a, "units") - metricDelta(b, "units"),
    )[0];

    return {
      turnedOffProducts,
      promotedProducts,
      turnedOffTotals,
      promotedTotals,
      turnedOffMonthly,
      promotedMonthly,
      allTargetsTotals,
      strongestTarget,
      weakestTarget,
    };
  }, [data]);

  if (error) {
    return (
      <div className="min-h-screen bg-stone-100 px-4 py-10 dark:bg-stone-950">
        <div className="mx-auto max-w-3xl rounded-lg border border-rose-200 bg-white p-6 text-rose-900 shadow-sm dark:border-rose-900 dark:bg-stone-900 dark:text-rose-100">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
            <h1 className="text-xl font-semibold">Hess SKU YoY dashboard failed to load</h1>
          </div>
          <p className="mt-3 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!data || !analysis) {
    return (
      <div className="min-h-screen bg-stone-100 px-4 py-10 dark:bg-stone-950">
        <div className="mx-auto max-w-3xl rounded-lg border border-stone-300 bg-white p-6 text-stone-700 shadow-sm dark:border-stone-700 dark:bg-stone-900 dark:text-stone-200">
          Loading Hess SKU YoY dashboard...
        </div>
      </div>
    );
  }

  const monthLabels = data.monthlyTotals.map((month) => monthLabel(month.monthStart));
  const chartMetricKey = metricMode === "units" ? "currentUnits" : "currentSales";
  const chartYagoKey = metricMode === "units" ? "yagoUnits" : "yagoSales";
  const chartDeltaFormatter =
    metricMode === "units" ? formatSignedNumber : formatSignedMoney;
  const chartValueFormatter =
    metricMode === "units" ? formatNumber : formatCompactMoney;

  const portfolioDatasets: BarChartDataset[] = [
    {
      type: "bar",
      order: 2,
      yAxisID: "y",
      label: metricMode === "units" ? "Current Units" : "Current Sales",
      data: data.monthlyTotals.map((month) => month[chartMetricKey]),
      backgroundColor: PORTFOLIO_COLOR,
      valueFormatter: chartValueFormatter,
      dataLabelFormatter: chartValueFormatter,
    },
    {
      type: "bar",
      order: 3,
      yAxisID: "y",
      label: "YAGO",
      data: data.monthlyTotals.map((month) => month[chartYagoKey]),
      backgroundColor: YAGO_COLOR,
      valueFormatter: chartValueFormatter,
      dataLabelFormatter: chartValueFormatter,
    },
    {
      type: "line",
      order: 1,
      yAxisID: "y1",
      label: "YoY %",
      data: data.monthlyTotals.map((month) => (metricPct(month, metricMode) ?? 0) * 100),
      borderColor: "#B45309",
      borderWidth: 2,
      pointBackgroundColor: "#B45309",
      tension: 0.32,
      dataLabelFormatter: (value) => `${value > 0 ? "+" : ""}${value.toFixed(0)}%`,
    },
  ];

  const cohortDatasets: BarChartDataset[] = [
    {
      label: "Turned off YoY delta",
      data: analysis.turnedOffMonthly.map((month) => metricDelta(month, metricMode)),
      backgroundColor: TURNOFF_COLOR,
      valueFormatter: chartDeltaFormatter,
      dataLabelFormatter: chartDeltaFormatter,
    },
    {
      label: "Kept / added YoY delta",
      data: analysis.promotedMonthly.map((month) => metricDelta(month, metricMode)),
      backgroundColor: PROMOTED_COLOR,
      valueFormatter: chartDeltaFormatter,
      dataLabelFormatter: chartDeltaFormatter,
    },
  ];

  return (
    <div className="min-h-screen bg-[#f5f3ee] text-stone-950 dark:bg-[#161312] dark:text-stone-50">
      <section className="border-b border-stone-300 bg-[#fbfaf7] dark:border-stone-800 dark:bg-[#211d1c]">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-4xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-stone-600 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-300">
                  <BarChart3 className="h-4 w-4" aria-hidden="true" />
                  Hess all-SKU sales
                </span>
                <span className="inline-flex rounded-full border border-amber-700/30 bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-amber-900 dark:border-amber-300/30 dark:bg-amber-950/40 dark:text-amber-100">
                  Post June 2025 change
                </span>
              </div>
              <h1 className="mt-5 text-4xl font-semibold tracking-normal text-stone-950 dark:text-white sm:text-5xl">
                Hess SKU YoY Impact Dashboard
              </h1>
              <p className="mt-3 max-w-3xl text-base leading-7 text-stone-600 dark:text-stone-300">
                {monthRangeLabel(data.dateRangeStart, data.dateRangeEnd)} performance for promoted, paused, and full portfolio SKUs.
              </p>
            </div>

            <div className="inline-flex w-full rounded-lg border border-stone-300 bg-white p-1 shadow-sm dark:border-stone-700 dark:bg-stone-900 sm:w-auto">
              {(["units", "sales"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  aria-pressed={metricMode === mode}
                  onClick={() => setMetricMode(mode)}
                  className={`h-10 flex-1 rounded-md px-5 text-sm font-semibold capitalize transition sm:flex-none ${
                    metricMode === mode
                      ? "bg-slate-900 text-white dark:bg-stone-100 dark:text-stone-950"
                      : "text-stone-600 hover:text-stone-950 dark:text-stone-300 dark:hover:text-white"
                  }`}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        <aside className="rounded-lg border border-amber-700/30 bg-amber-50 p-4 text-sm text-amber-950 shadow-sm dark:border-amber-300/30 dark:bg-amber-950/30 dark:text-amber-100">
          <div className="flex gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <div>
              <div className="font-semibold">Source and matching caveat</div>
              <p className="mt-1 leading-6">
                {data.skuCodeQualityNote} The source starts in July 2025, so this view compares the post-change period to year-ago values; it does not contain a pre-June baseline inside the same file.
              </p>
            </div>
          </div>
        </aside>

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <KpiTile
            title="Portfolio Units"
            value={formatNumber(data.summary.currentUnits)}
            delta={`${formatSignedNumber(data.summary.unitDelta)} YoY`}
            detail={formatPct(data.summary.unitDeltaPct)}
            icon={TrendingUp}
            tone="portfolio"
          />
          <KpiTile
            title="Portfolio Sales"
            value={formatMoney(data.summary.currentSales)}
            delta={`${formatSignedMoney(data.summary.salesDelta)} YoY`}
            detail={formatPct(data.summary.salesDeltaPct)}
            icon={BarChart3}
            tone="portfolio"
          />
          <KpiTile
            title="Kept / Added SKUs"
            value={formatMetricValue(metricCurrent(analysis.promotedTotals, metricMode), metricMode)}
            delta={`${formatSignedMetricValue(metricDelta(analysis.promotedTotals, metricMode), metricMode)} YoY`}
            detail={formatPct(metricPct(analysis.promotedTotals, metricMode))}
            icon={Megaphone}
            tone="good"
          />
          <KpiTile
            title="Turned Off SKUs"
            value={formatMetricValue(metricCurrent(analysis.turnedOffTotals, metricMode), metricMode)}
            delta={`${formatSignedMetricValue(metricDelta(analysis.turnedOffTotals, metricMode), metricMode)} YoY`}
            detail={formatPct(metricPct(analysis.turnedOffTotals, metricMode))}
            icon={CircleOff}
            tone={metricDelta(analysis.turnedOffTotals, metricMode) >= 0 ? "good" : "risk"}
          />
        </section>

        <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="rounded-lg border border-stone-300 bg-white p-5 shadow-sm dark:border-stone-700 dark:bg-[#211d1c]">
            <div className="flex items-center gap-2 text-teal-700 dark:text-teal-300">
              <ArrowUpRight className="h-5 w-5" aria-hidden="true" />
              <h2 className="text-lg font-semibold">Largest target gain</h2>
            </div>
            <div className="mt-4 text-2xl font-semibold text-stone-950 dark:text-stone-50">
              {analysis.strongestTarget.name}
            </div>
            <div className="mt-2 text-sm text-stone-600 dark:text-stone-300">
              {formatSignedMetricValue(metricDelta(analysis.strongestTarget, metricMode), metricMode)} ({formatPct(metricPct(analysis.strongestTarget, metricMode))})
            </div>
          </div>
          <div className="rounded-lg border border-stone-300 bg-white p-5 shadow-sm dark:border-stone-700 dark:bg-[#211d1c]">
            <div className="flex items-center gap-2 text-rose-700 dark:text-rose-300">
              <ArrowDownRight className="h-5 w-5" aria-hidden="true" />
              <h2 className="text-lg font-semibold">Largest target drag</h2>
            </div>
            <div className="mt-4 text-2xl font-semibold text-stone-950 dark:text-stone-50">
              {analysis.weakestTarget.name}
            </div>
            <div className="mt-2 text-sm text-stone-600 dark:text-stone-300">
              {formatSignedMetricValue(metricDelta(analysis.weakestTarget, metricMode), metricMode)} ({formatPct(metricPct(analysis.weakestTarget, metricMode))})
            </div>
          </div>
          <div className="rounded-lg border border-stone-300 bg-white p-5 shadow-sm dark:border-stone-700 dark:bg-[#211d1c]">
            <div className="flex items-center gap-2 text-slate-700 dark:text-stone-300">
              {metricDelta(analysis.allTargetsTotals, metricMode) >= 0 ? (
                <TrendingUp className="h-5 w-5" aria-hidden="true" />
              ) : (
                <TrendingDown className="h-5 w-5" aria-hidden="true" />
              )}
              <h2 className="text-lg font-semibold">Target SKU net</h2>
            </div>
            <div className={`mt-4 text-2xl font-semibold ${deltaColorClass(metricDelta(analysis.allTargetsTotals, metricMode))}`}>
              {formatSignedMetricValue(metricDelta(analysis.allTargetsTotals, metricMode), metricMode)}
            </div>
            <div className="mt-2 text-sm text-stone-600 dark:text-stone-300">
              {formatPct(metricPct(analysis.allTargetsTotals, metricMode))} across {data.targetProducts.length} watched SKUs
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <CohortPanel
            title="Promotions turned off"
            subtitle="SKUs listed as sponsored product promotions turned off in June 2025."
            icon={CircleOff}
            products={analysis.turnedOffProducts}
            totals={analysis.turnedOffTotals}
            metricMode={metricMode}
            color={TURNOFF_COLOR}
            softColor={TURNOFF_SOFT}
          />
          <CohortPanel
            title="Kept on or added"
            subtitle="SKUs kept in, or added to, promoted product campaigns."
            icon={Megaphone}
            products={analysis.promotedProducts}
            totals={analysis.promotedTotals}
            metricMode={metricMode}
            color={PROMOTED_COLOR}
            softColor={PROMOTED_SOFT}
          />
        </section>

        <section className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <div className="rounded-lg border border-stone-300 bg-white p-5 shadow-sm dark:border-stone-700 dark:bg-[#211d1c]">
            <h2 className="text-lg font-semibold text-stone-950 dark:text-stone-50">
              Monthly Portfolio YoY
            </h2>
            <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">
              Current period compared with the same month one year ago.
            </p>
            <div className="mt-5">
              <BarChart
                labels={monthLabels}
                datasets={portfolioDatasets}
                height="h-80"
                showLegend
                showDataLabels={false}
                valueFormatter={chartValueFormatter}
              />
            </div>
          </div>

          <div className="rounded-lg border border-stone-300 bg-white p-5 shadow-sm dark:border-stone-700 dark:bg-[#211d1c]">
            <h2 className="text-lg font-semibold text-stone-950 dark:text-stone-50">
              Target SKU YoY Delta by Month
            </h2>
            <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">
              Cohort-level gain or drag after the June 2025 promotion change.
            </p>
            <div className="mt-5">
              <BarChart
                labels={monthLabels}
                datasets={cohortDatasets}
                height="h-80"
                showLegend
                showDataLabels={false}
                valueFormatter={chartDeltaFormatter}
              />
            </div>
          </div>
        </section>

        <TargetSkuTable products={data.targetProducts} metricMode={metricMode} />
        <AllSkuTable products={data.allProducts} metricMode={metricMode} />
      </main>
    </div>
  );
};
